import React, { useState } from 'react';
import { supabase } from '../global/supabase';
import HamburgerMenu from '../global/menu';
import '../global/styles.css';

function ChainOfCustodyReport({ userInfo, isAdmin, onLogout, onNavigate }) {
  const [selectedSite, setSelectedSite] = useState('mayo_21');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // Site options
  const sites = [
    { value: 'mayo_21', label: 'Mayo 21' },
    { value: 'mayo_22', label: 'Mayo 22' },
    { value: 'mayo_23', label: 'Mayo 23' },
    { value: 'mayo_24', label: 'Mayo 24' },
    { value: 'mayo_25', label: 'Mayo 25' }
  ];

  // Access control
  if (!isAdmin) {
    return (
      <div className="container-center">
        <div className="card card-center">
          <h1 className="title">Access Denied</h1>
          <p className="subtitle">You do not have administrator privileges.</p>
          <button onClick={() => onNavigate('home')} className="btn btn-primary">
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const runReport = async () => {
    if (!startDate || !endDate) {
      alert('Please select both start and end dates.');
      return;
    }

    setLoading(true);
    setReportData([]);

    try {
      const start = new Date(startDate);
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);

      // Query the selected site table
      const { data, error } = await supabase
        .from(selectedSite)
        .select('*')
        .not('first_name', 'is', null)
        .not('last_name', 'is', null)
        .order('created_at', { ascending: false });

      if (error) {
        console.error(`Error querying ${selectedSite}:`, error);
        alert('Error loading data. Please try again.');
        setLoading(false);
        return;
      }

      // Filter by date range
      const filtered = (data || [])
        .map(record => {
          const recordDate = new Date(record.created_at);
          return { ...record, recordDate };
        })
        .filter(record => {
          return record.recordDate >= start && record.recordDate <= end;
        })
        .map(record => ({
          location: record.location || sites.find(s => s.value === selectedSite)?.label || '',
          date: record.recordDate.toLocaleDateString('en-US'),
          time: record.recordDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
          lastName: record.last_name || '',
          firstName: record.first_name || '',
          sealStatus: record.seal_intact || '',
          sealNumber: record.seal_number || '',
          reason: record.reason_broken_seal || ''
        }));

      setReportData(filtered);
    } catch (error) {
      console.error('Error running report:', error);
      alert('Error running report. Please check console for details.');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (column) => {
    let direction = 'asc';
    if (sortConfig.key === column && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key: column, direction });

    const sortedData = [...reportData].sort((a, b) => {
      if (a[column] < b[column]) return direction === 'asc' ? -1 : 1;
      if (a[column] > b[column]) return direction === 'asc' ? 1 : -1;
      return 0;
    });

    setReportData(sortedData);
  };

  const exportToCSV = () => {
    if (reportData.length === 0) {
      alert('No data to export.');
      return;
    }

    const headers = ['Location', 'Date', 'Time', 'Last Name', 'First Name', 'Seal Status', 'Seal Number', 'Reason'];
    const csvData = [
      headers.join(','),
      ...reportData.map(row =>
        [row.location, row.date, row.time, row.lastName, row.firstName, row.sealStatus, row.sealNumber, row.reason]
          .map(field => `"${field}"`)
          .join(',')
      )
    ].join('\n');

    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `chain_of_custody_${selectedSite}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getSortIcon = (column) => {
    if (sortConfig.key !== column) return '↕';
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f8f9fa' }}>
      <HamburgerMenu isAdmin={isAdmin} onLogout={onLogout} onNavigate={onNavigate} />

      <div style={{ padding: '80px 20px 20px', maxWidth: '1400px', margin: '0 auto' }}>
        {/* Back Button */}
        <button
          onClick={() => onNavigate('admin-dashboard')}
          style={{
            padding: '8px 16px',
            border: '2px solid #0E3293',
            borderRadius: '6px',
            background: 'white',
            color: '#0E3293',
            fontSize: '13px',
            fontWeight: '600',
            cursor: 'pointer',
            marginBottom: '20px'
          }}
        >
          ← Back to Admin
        </button>

        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #0E3293 0%, #1A4DBB 100%)',
          color: 'white',
          padding: '30px',
          borderRadius: '10px',
          marginBottom: '30px',
          textAlign: 'center',
          boxShadow: '0 4px 12px rgba(14,50,147,0.3)'
        }}>
          <h1 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '10px', color: 'white' }}>
            Chain of Custody Report
          </h1>
          <p style={{ fontSize: '14px', margin: 0, color: 'rgba(255,255,255,0.95)', lineHeight: '1.6', maxWidth: '800px', marginLeft: 'auto', marginRight: 'auto' }}>
            Detailed record of narcotic handling activities across locations, including dates, times, and personnel involved, ensuring a clear audit trail for compliance and accountability.
          </p>
        </div>

        {/* Filters */}
        <div className="user-action-bar report-filters" style={{
          background: 'white',
          padding: '20px',
          borderRadius: '10px',
          marginBottom: '20px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          display: 'flex',
          gap: '15px',
          flexWrap: 'wrap',
          alignItems: 'end'
        }}>
          <div style={{ flex: '1 1 180px' }}>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '6px', color: '#333' }}>
              Site / Location
            </label>
            <select
              value={selectedSite}
              onChange={(e) => setSelectedSite(e.target.value)}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                fontSize: '13px',
                background: 'white',
                cursor: 'pointer'
              }}
            >
              {sites.map(site => (
                <option key={site.value} value={site.value}>
                  {site.label}
                </option>
              ))}
            </select>
          </div>

          <div style={{ flex: '1 1 180px' }}>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '6px', color: '#333' }}>
              Start Date
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                fontSize: '13px'
              }}
            />
          </div>

          <div style={{ flex: '1 1 180px' }}>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '6px', color: '#333' }}>
              End Date
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                fontSize: '13px'
              }}
            />
          </div>

          <div style={{ flex: '0 1 auto' }}>
            <button
              onClick={runReport}
              disabled={loading}
              style={{
                padding: '10px 20px',
                border: 'none',
                borderRadius: '6px',
                background: loading ? '#ccc' : '#0E3293',
                color: 'white',
                fontSize: '13px',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer'
              }}
            >
              {loading ? 'Running...' : 'Run Report'}
            </button>
          </div>

          <div style={{ flex: '0 1 auto' }}>
            <button
              onClick={exportToCSV}
              disabled={reportData.length === 0}
              style={{
                padding: '10px 20px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                background: 'white',
                color: '#333',
                fontSize: '13px',
                fontWeight: '600',
                cursor: reportData.length === 0 ? 'not-allowed' : 'pointer'
              }}
            >
              Export to CSV
            </button>
          </div>
        </div>

        {/* Loading Indicator */}
        {loading && (
          <div style={{
            background: 'white',
            padding: '40px',
            borderRadius: '10px',
            textAlign: 'center',
            marginBottom: '20px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
          }}>
            <p style={{ color: '#0E3293', fontSize: '16px', fontWeight: '500' }}>
              Generating chain of custody report...
            </p>
          </div>
        )}

        {/* Results Table */}
        {reportData.length > 0 && (
          <div className="report-table-container" style={{
            background: 'white',
            borderRadius: '10px',
            overflow: 'auto',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
          }}>
            <div style={{ padding: '20px 20px 10px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#333', margin: 0 }}>
                Custody Records ({reportData.length} entries found)
              </h3>
              <p style={{ fontSize: '13px', color: '#666', marginTop: '5px', marginBottom: 0 }}>
                {sites.find(s => s.value === selectedSite)?.label} - {startDate} to {endDate}
              </p>
            </div>
            <div className="table-scroll" style={{ overflowX: 'auto' }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '13px'
              }}>
                <thead>
                  <tr style={{ background: '#f8f9fa' }}>
                    {[
                      { key: 'location', label: 'Location' },
                      { key: 'date', label: 'Date' },
                      { key: 'time', label: 'Time' },
                      { key: 'lastName', label: 'Last Name' },
                      { key: 'firstName', label: 'First Name' },
                      { key: 'sealStatus', label: 'Seal Status' },
                      { key: 'sealNumber', label: 'Seal #' },
                      { key: 'reason', label: 'Reason' }
                    ].map(column => (
                      <th
                        key={column.key}
                        onClick={() => handleSort(column.key)}
                        style={{
                          padding: '12px',
                          textAlign: 'left',
                          cursor: 'pointer',
                          borderBottom: '2px solid #0E3293',
                          fontWeight: '600',
                          color: '#333',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {column.label} {getSortIcon(column.key)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {reportData.map((row, index) => (
                    <tr key={index} style={{
                      borderBottom: '1px solid #e0e0e0',
                      backgroundColor: index % 2 === 0 ? 'white' : '#f8f9fa'
                    }}>
                      <td style={{ padding: '12px', color: '#333' }}>{row.location}</td>
                      <td style={{ padding: '12px', color: '#333' }}>{row.date}</td>
                      <td style={{ padding: '12px', color: '#333' }}>{row.time}</td>
                      <td style={{ padding: '12px', color: '#333' }}>{row.lastName}</td>
                      <td style={{ padding: '12px', color: '#333' }}>{row.firstName}</td>
                      <td style={{ padding: '12px', color: '#333' }}>
                        <span style={{
                          padding: '3px 8px',
                          borderRadius: '4px',
                          fontSize: '11px',
                          fontWeight: '600',
                          background: row.sealStatus === 'yes' ? '#e8f5e9' : row.sealStatus === 'no' ? '#ffebee' : '#f5f5f5',
                          color: row.sealStatus === 'yes' ? '#2e7d32' : row.sealStatus === 'no' ? '#c62828' : '#666'
                        }}>
                          {row.sealStatus === 'yes' ? 'Intact' : row.sealStatus === 'no' ? 'Broken' : '-'}
                        </span>
                      </td>
                      <td style={{ padding: '12px', color: '#333', fontFamily: 'monospace', fontSize: '12px' }}>{row.sealNumber}</td>
                      <td style={{ padding: '12px', color: '#666', fontSize: '12px', maxWidth: '200px' }}>{row.reason || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* No Results Message */}
        {!loading && reportData.length === 0 && startDate && endDate && (
          <div style={{
            background: 'white',
            padding: '40px',
            borderRadius: '10px',
            textAlign: 'center',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
          }}>
            <p style={{ color: '#666', fontSize: '16px' }}>
              No custody records found for the selected criteria.
            </p>
          </div>
        )}
      </div>

      <footer style={{
        textAlign: 'center',
        padding: '20px',
        color: '#666',
        fontSize: '12px'
      }}>
        <p>Chain of Custody Report - NarcoticTrack © 2025</p>
      </footer>
    </div>
  );
}

export default ChainOfCustodyReport;
